import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { StepType, useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { useAILoadingContext } from '../AIFormStepContext'
import keys from 'constants/keys'
import useLocaleText from 'components/useLocaleText'
const SUBTASK_PATH = 'sub_tasks[0]'
const EMPTY_IMAGES: string[] = []
export interface IAISubtaskStepData {
  title: string
  sub_tasks: any[]
}

export interface IAISubtaskStepProps {
  /**
   * formData slice this step reads and writes. Defaults to the task slice (AI task creation, where the
   * generated subtask IS the task). The VM sub-flow points it at a scratch slice so the step cannot
   * overwrite the real task and its list of subtasks.
   */
  draftKey?: string
  /** Audience / schedule / briefing summary — only meaningful when the step creates a whole task. */
  showSummary?: boolean
  /** Task title editor — hidden in the VM sub-flow, where the task title is owned by step 1. */
  showTaskTitle?: boolean
  /** Overrides the CTA behaviour. Default (AI task creation): persist and publish the task. */
  onDone?: (data: IAISubtaskStepData) => void
  onDraftChange?: (data: IAISubtaskStepData) => void
}

export function useAiPreviewForm({
  draftKey = StepType.SUBTASK_TASK_CREATION,
  onDone,
  onDraftChange,
}: IAISubtaskStepProps) {
  const { setCurrentFormSubmit, updateFormData, formData, submitAllForms } = useFormStepContext()
  const { isGenerating, selectedSubtaskType } = useAILoadingContext()

  const t_ai_task_title_vm = useLocaleText('step2_title_vm')
  const t_ai_task_title_qst = useLocaleText('step2_title_qst')
  const t_ai_message_msg = useLocaleText('ai_subtask_msg')
  const t_ai_message = useLocaleText('ai_subtask_title')
  const t_ai_generating_header = useLocaleText('ai_creating')
  const t_ai_generating_subtitle = useLocaleText('ai_generating_msg_1')

  const isQuestion = selectedSubtaskType === 'question'
  const subtaskType = isQuestion ? keys.SUBTASK_TYPES.OPEN_QUESTION : keys.SUBTASK_TYPES.IMAGE_SUBTASK

  const saved = (formData as any)[draftKey]
  const savedSubtask = saved?.sub_tasks?.[0]

  // When isGenerating=true (LLM in-flight): start empty so loader shows blank fields.
  // When isGenerating=false: initialize from saved data so TipTap gets correct content
  // at creation time — reset() alone is not enough because TipTap uses `content` only once.
  const methods = useForm({
    defaultValues: {
      title: isGenerating ? '' : saved?.title || '',
      sub_tasks: [
        {
          title: isGenerating ? undefined : savedSubtask?.title ?? undefined,
          type: subtaskType,
          order_index: 0,
          is_required: true,
          content: {
            rich_text: isGenerating ? null : savedSubtask?.content?.rich_text || null,
            images: isGenerating ? [] : savedSubtask?.content?.images || [],
          },
        },
      ] as any[],
    },
  })

  const { handleSubmit, reset, getValues, watch, triggerValidation } = methods

  // RHF v5: sets isWatchAllRef=true so every setValue() (incl. TipTap) triggers reRender()
  watch() // eslint-disable-line @typescript-eslint/no-unused-expressions

  const step2Images: string[] = watch(`${SUBTASK_PATH}.content.images`) || EMPTY_IMAGES

  const latestValuesRef = useRef<{ title: string; sub_tasks: any[] }>({ title: '', sub_tasks: [] })
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone
  const onDraftChangeRef = useRef(onDraftChange)
  onDraftChangeRef.current = onDraftChange
  const previousDraft = useRef('')

  // Eagerly sync step 2 images → step 1 reference_images so defaultValues in step 1
  // sees them when the user navigates back (before any cleanup fires).
  useEffect(() => {
    if (isGenerating) return
    updateFormData('AI_PROMPT' as any, {
      ...(formData as any)?.['AI_PROMPT'],
      reference_images: step2Images,
    })
  }, [step2Images]) // eslint-disable-line react-hooks/exhaustive-deps

  // After every render (fields still registered), persist the current form state into the ref.
  // We read the flat object from getValues() and reconstruct the nested shape manually.
  useEffect(() => {
    if (isGenerating) return
    const flat = getValues() as Record<string, any>
    if (Object.keys(flat).length === 0) return
    latestValuesRef.current = {
      title: flat.title || '',
      sub_tasks: [
        {
          title: flat['sub_tasks[0].title'] || '',
          type: flat['sub_tasks[0].type'] ?? subtaskType,
          order_index: flat['sub_tasks[0].order_index'] ?? 0,
          is_required: flat['sub_tasks[0].is_required'] ?? true,
          content: {
            rich_text: flat['sub_tasks[0].content.rich_text'] ?? null,
            images: flat['sub_tasks[0].content.images'] || [],
          },
        },
      ],
    }
    const serialized = JSON.stringify(latestValuesRef.current)
    if (serialized !== previousDraft.current) {
      previousDraft.current = serialized
      onDraftChangeRef.current?.(latestValuesRef.current)
    }
  }) // intentionally no deps — runs after every render

  // On unmount: save from ref (which was last updated when fields were still registered).
  useEffect(() => {
    return () => {
      updateFormData(draftKey as any, latestValuesRef.current)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // After LLM generation completes, apply result (or defaults on error) into the form
  useEffect(() => {
    if (isGenerating) return
    const freshSaved = (formData as any)[draftKey]
    const freshSubtask = freshSaved?.sub_tasks?.[0]
    reset({
      title: freshSaved?.title || (isQuestion ? t_ai_task_title_qst : t_ai_task_title_vm),
      sub_tasks: [
        {
          title: freshSubtask?.title ?? undefined,
          type: subtaskType,
          order_index: 0,
          is_required: true,
          content: {
            rich_text: freshSubtask?.content?.rich_text || null,
            images: freshSubtask?.content?.images || [],
          },
        },
      ],
    })
  }, [isGenerating]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentFormSubmit(() => async () => {
      if (onDoneRef.current && onDraftChangeRef.current) {
        await triggerValidation()
        onDoneRef.current(latestValuesRef.current)
        return
      }
      return handleSubmit((data) => {
        const values = { title: data.title, sub_tasks: data.sub_tasks } as IAISubtaskStepData
        if (onDoneRef.current) {
          onDoneRef.current(values)
          return
        }
        updateFormData(draftKey as any, values)
        submitAllForms()
      })()
    })
  }, [handleSubmit, triggerValidation, setCurrentFormSubmit, updateFormData, submitAllForms, draftKey])

  return {
    methods,
    isGenerating,
    isQuestion,
    t_ai_message,
    t_ai_message_msg,
    t_ai_generating_header,
    t_ai_generating_subtitle,
  }
}
